// CourseView — gate + (when ready) shell with sub-routes.
//
// Decision history:
//   Session 19E (§4): single component, status state machine, ResultView render.
//   Session 19F (§4.8): added paste-JSON state.
//   Session 19G (§5.3): split conceptually: the gate logic stays here (loading /
//     not-found / idle / paste / generating / error). When a result is cached,
//     this component renders a Provider-wrapped shell (header + tabs) with an
//     <Outlet /> so React Router renders sub-routes.
//   Session 47: gate UI components extracted to course-gate-components.tsx;
//     ingest orchestration extracted to lib/hooks/use-course-ingest.ts.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CourseDraft, CourseProgress } from '@examforge/shared/schemas';
import {
  getDraft,
  getResult,
  getProgress,
  type IngestResultRecord,
} from '../storage/idb.js';
import { computeSourceHash, isPackageStale } from '../lib/source-hash.js';
import { PasteJsonInput } from '../components/paste-json-input.js';
import { IngestProgress } from '../components/ingest-progress.js';
import { StateCard } from '../components/state-card.js';
import { generateErrorId } from '../lib/error-id.js';
import { useT } from '../i18n/index.js';
import { useNotify } from '../lib/hooks/use-notify.js';
import { CourseShell } from './course-shell.js';
import { OutlineReviewView } from './outline-review.js';
import {
  CourseGate,
  CenterMsg,
  CourseDraftSummary,
  ExtractingView,
} from './course-gate-components.js';
// Isomorphism Phase 3: dual-write Zustand alongside Context during migration.
import { useStore } from '../lib/store/index.js';
import { useCourseIngest } from '../lib/hooks/use-course-ingest.js';

// Session 19F backdoor — see DEVLOG 19F. Flip / env-gate to hide later.
const PASTE_JSON_ENABLED = true;

type Status = 'loading' | 'idle' | 'extracting' | 'reviewing' | 'paste' | 'generating' | 'done' | 'error' | 'not-found';

export function CourseView() {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [draft, setDraft] = useState<CourseDraft | null>(null);
  const [resultRecord, setResultRecord] = useState<IngestResultRecord | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  // Session 4: errorId for StateCard error variant — admin lookup handle.
  const [errorId, setErrorId] = useState<string | null>(null);

  // ── Ingest orchestration (extracted hook) ───────────────────────────────
  const {
    generate, startExtract, confirmOutline,
    cancelIngest, acceptPasted, regenerateConfirm,
    hydrateFromRecord,
    ingestProgress, lintReport, outlineDraft,
  } = useCourseIngest(draft, t, {
    onStatusChange: (s) => setStatus(s),
    onError: (e) => { setError(e); setErrorId(generateErrorId()); },
    onResultReady: (r) => setResultRecord(r),
  });

  // ── View-level state management ─────────────────────────────────────────

  const refreshProgress = useCallback(async (): Promise<void> => {
    if (!id) return;
    const p = await getProgress(id);
    setProgress(p);
    // Isomorphism Phase 3: sync to Zustand so Event Bus subscribers get fresh data.
    useStore.getState().setProgress(p);
  }, [id]);

  useEffect(() => {
    if (!id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return guard for missing id; the synchronous setStatus here is a terminal branch, the main flow is async fetch+setState below (not flagged by the rule).
      setStatus('not-found');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [d, r, p] = await Promise.all([getDraft(id), getResult(id), getProgress(id)]);
        if (cancelled) return;
        if (!d) {
          setStatus('not-found');
          return;
        }
        setDraft(d);
        // 19BO+ Line 2 B4.4 step 5: hydrate progress from cloud before showing.
        // If the user opted into progress-cloud-sync (account pref, server-side),
        // pull field-level events newer than local.updatedAt and LWW-merge them.
        // Fire-and-forget resilience: failure → fall back to local (p) silently.
        // Read store state live (not from closure) so this effect doesn't depend
        // on currentUser/progressCloudSync (those changing must not reload the course).
        let progressToShow = p;
        let pendingHydratedCount = 0;
        const { currentUser, progressCloudSync } = useStore.getState();
        if (currentUser && !currentUser.isAnonymous && progressCloudSync) {
          const sinceMs = Date.parse(p.updatedAt) || 0;
          const { hydrateProgressFromCloud } = await import('../lib/hydrate-progress.js');
          const { putProgress } = await import('../storage/idb.js');
          const { progress: merged, appliedCount } = await hydrateProgressFromCloud(id, p, sinceMs);
          if (appliedCount > 0) {
            // Events arrived → persist the merged state + surface it.
            progressToShow = merged;
            pendingHydratedCount = appliedCount;
            // Only write back if the component is still mounted (not cancelled).
            if (!cancelled) {
              try { await putProgress(merged); } catch { /* non-critical */ }
            }
          }
        }
        if (cancelled) return;
        setProgress(progressToShow);
        useStore.getState().setProgress(progressToShow);
        // B5.4 §S2: one-shot toast when cloud data was merged (not when empty).
        // Silent when appliedCount === 0 (no new data / pull failed gracefully).
        if (pendingHydratedCount > 0) {
          notify({
            source: 'system',
            level: 'info',
            title: t('sync.hydrated', { count: pendingHydratedCount }),
            persistent: false,
          });
        }
        if (r) {
          setResultRecord(r);
          // DEBT-19BO+-LINTREPORT-IDB-PERSIST (批次 C4): restore lintReport from
          // IDB so the verdict survives reload (done_low/high_conf no longer
          // degrades to done_unknown after refresh).
          hydrateFromRecord(r);
          setStatus('done');
        } else {
          setStatus('idle');
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
          setErrorId(generateErrorId());
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // hydrateFromRecord is a useCallback([]) stable reference — safe in deps
    // without triggering extra effect runs (it only calls setLintReport).
    // notify (useNotify → useCallback stable) and t (useT → useCallback stable,
    // rebuilt only on locale change) are likewise safe here — body reads both
    // for one-shot hydrate notifications.
  }, [id, hydrateFromRecord, notify, t]);

  // 19BI+ · autoExtract: when navigated from /create with ?autoExtract=1,
  // automatically start the outline extraction without user clicking "Generate".
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (status === 'idle' && searchParams.get('autoExtract') === '1' && draft) {
      // Remove the param so refresh / back-navigation doesn't re-trigger.
      window.history.replaceState(null, '', `/course/${draft.id}`);
      startExtract();
    }
    // 19BM-C: startExtract (useCallback from useCourseIngest) intentionally
    // omitted — including it would re-fire on every hook re-render. The
    // effect self-guards via `status === 'idle'` + replaceState stripping the
    // autoExtract param, so it runs at most once per navigation. startExtract
    // captures stable refs internally (cb.current pattern) so a stale closure
    // here is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchParams, draft]);

  // ── Status → render ────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <CourseGate>
        <CenterMsg heading={t('course.loading')} />
      </CourseGate>
    );
  }
  if (status === 'not-found') {
    return (
      <CourseGate>
        <CenterMsg
          heading={t('course.notFound')}
          body={t('course.notFoundHint')}
          action={{ label: t('course.backHome'), onClick: () => navigate('/') }}
        />
      </CourseGate>
    );
  }
  if (!draft || !progress) return null;

  if (status === 'idle') {
    return (
      <CourseGate>
        <main className="course-main">
          <CourseDraftSummary draft={draft} />
          <section className="course-cta">
            <h2>{t('course.generateHeading')}</h2>
            <p>{t('course.generateBody')}</p>
            <button className="btn-primary btn-primary-lg" onClick={startExtract}>
              {t('course.generateNow')}
            </button>
            <div className="course-cta-links">
              <button type="button" className="btn-link" onClick={() => void generate()}>
                {t('course.skipReview') ?? 'Skip review (auto)'}
              </button>
              {PASTE_JSON_ENABLED && (
                <button
                  type="button"
                  className="btn-link course-cta-alt"
                  onClick={() => setStatus('paste')}
                >
                  {t('course.pasteJsonAlt')}
                </button>
              )}
            </div>
          </section>
        </main>
      </CourseGate>
    );
  }

  if (status === 'extracting') {
    return (
      <CourseGate>
        <ExtractingView
          draft={draft}
          onCancel={() => setStatus('idle')}
        />
      </CourseGate>
    );
  }

  if (status === 'reviewing' && outlineDraft) {
    return (
      <CourseGate>
        <main className="course-main">
          <OutlineReviewView
            outline={outlineDraft}
            courseTitle={draft.title}
            onConfirm={confirmOutline}
            onCancel={() => setStatus('idle')}
          />
        </main>
      </CourseGate>
    );
  }

  if (status === 'paste') {
    return (
      <CourseGate>
        <main className="course-main">
          <CourseDraftSummary draft={draft} />
          <PasteJsonInput
            onCancel={() => setStatus(resultRecord ? 'done' : 'idle')}
            onAccept={acceptPasted}
          />
        </main>
      </CourseGate>
    );
  }

  if (status === 'generating') {
    return (
      <CourseGate>
        <IngestProgress state={ingestProgress} onCancel={cancelIngest} lintReport={lintReport} />
      </CourseGate>
    );
  }

  if (status === 'error') {
    return (
      <CourseGate>
        <main className="course-main">
          <CourseDraftSummary draft={draft} />
          {/* Session 4: migrated custom .course-cta-error to StateCard error variant.
           *  ingest is the longest/most-expensive op (30-45min) — errorId here is
           *  highest value for admin log lookup. Three buttons → two: retry
           *  (onRetry) + paste-JSON (action); back-home dropped (browser back). */}
          <StateCard
            variant="error"
            title={t('course.errorHeading')}
            hint={error ?? undefined}
            errorId={errorId}
            onRetry={startExtract}
            retryLabel={t('course.errorRetry')}
            action={PASTE_JSON_ENABLED ? (
              <button className="btn-secondary" onClick={() => setStatus('paste')}>
                {t('course.pasteJsonAlt')}
              </button>
            ) : undefined}
          />
        </main>
      </CourseGate>
    );
  }

  if (status === 'done' && resultRecord) {
    const pkg = resultRecord.result.package;
    // LLM_NATIVE §3 stale check: hashes are FNV-1a 8 hex; cheap to compute every render.
    const currentDraftHash = computeSourceHash(draft);
    const isStale = isPackageStale(pkg.sourceHash, currentDraftHash);

    // Isomorphism Phase 3: write to Zustand (sole source of truth).
    // All sub-route components read from useStoreCourseData().
    const store = useStore.getState();
    if (store.draft !== draft) store.setDraft(draft);
    if (store.pkg !== pkg) store.setPackage(pkg);
    if (store.progress !== progress) store.setProgress(progress);
    if (store.isStale !== isStale) store.setStale(isStale);
    // Gate callbacks — closures that reference CourseView's local state.
    store.setGateCallbacks({
      refreshProgress,
      regenerate: async () => { await regenerateConfirm(); },
      switchToPaste: () => setStatus('paste'),
    });

    return <CourseShell />;
  }

  return null;
}
